import { createBrowserHistory } from 'history';
import { IParseOptions } from 'qs';
import { useMemo, useState } from 'react';

import { MobileRoutesParamsList } from '@webtrader/core/router/mobile';
import { ALL_MOBILE_ROUTES } from '@webtrader/core/router/mobile.web';
import { IWebQueryStringParams, queryStringParams } from '@webtrader/core/router/params/web';
import { IOrderFormData } from '@webtrader/core/services/form/order/service';

interface INavigateParams {
  defaultTab?: string;
  publicAuth?: boolean;
  accountName?: string;
  executeOrder?: () => void;
  formValues?: IOrderFormData;
  orderId?: string;
  title?: string;
  subTitle?: string;
  accountDetails?: string;
  setNeedUpdateFields?: (value: boolean) => void;
  // Check forgot password is from ELoginAs.Live or not
  isLive?: boolean;
}

export const history = createBrowserHistory();
let historyCounter = 0;

const storedNavigationParams: INavigateParams[] = [];

export const navigate = <RouteName extends keyof MobileRoutesParamsList>(
  route: RouteName | string,
  params?: INavigateParams
) => {
  if (params) {
    storedNavigationParams.push(params);
  }

  historyCounter += 1;
  history.push(route);
};

export const replace = <RouteName extends keyof MobileRoutesParamsList>(
  route: RouteName | string,
  params?: INavigateParams
) => {
  if (params) {
    storedNavigationParams.push(params);
  }

  history.replace(route);
};

export const goBack = () => {
  if (canGoBack()) {
    storedNavigationParams.pop();

    historyCounter -= 1;
    history.goBack();
  } else {
    // fallback to home screen when the navigation stack being cleared after user refresh the webpage
    navigate(ALL_MOBILE_ROUTES.BOTTOM_TAB.INDEX);
  }
};

export const canGoBack = () => historyCounter > 0;

export const goBackOrCallback = (callback: () => void) => {
  if (canGoBack()) {
    storedNavigationParams.pop();

    historyCounter -= 1;
    history.goBack();
  } else {
    callback();
  }
};

export const getCurrentRoute = () => history.location.pathname;

// Dummy method to override react-native navigation methods on react-native-web
export const useScreenRoute = () => {
  return history.location.pathname;
};

export const useSearchRouteParams = () => {
  return history.location.pathname;
};

export const useRouteQueryParamsSearch = (options?: IParseOptions): IWebQueryStringParams => {
  const search = history.location.search;
  const [qsOptions] = useState(options);
  const query = useMemo(() => queryStringParams(search, qsOptions), [search, qsOptions]);
  return query;
};

export const useTradeRouteParams = () => {
  return history.location.pathname;
};

export const useTransactionRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const storedParams = storedNavigationParams[0];
    storedNavigationParams.pop();

    return storedParams;
  }

  return undefined;
};

export const useDemoCreationRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const storedParams = storedNavigationParams[0];
    storedNavigationParams.pop();

    return storedParams;
  }

  return undefined;
};

export const useDemoCompletionRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const storedParams = storedNavigationParams[0];
    storedNavigationParams.pop();

    return storedParams;
  }

  return undefined;
};

export const useOneClickTradingConfirmationRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const params = storedNavigationParams.filter((p) => p.formValues && p.executeOrder);
    const storedParams = params[0];

    return storedParams;
  }

  return undefined;
};

export const useExperienceDemoCompletionRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const params = storedNavigationParams.filter(
      (p) => p.title && p.subTitle && p.accountDetails && p.setNeedUpdateFields
    );
    const storedParams = params[0];

    return storedParams;
  }

  return undefined;
};

export const useExperienceResetPasswordRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const params = storedNavigationParams.filter((p) => p.setNeedUpdateFields);
    const storedParams = params[0];

    return storedParams;
  }

  return undefined;
};

export const useExperienceDemoCreationRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const params = storedNavigationParams.filter((p) => p.setNeedUpdateFields);
    const storedParams = params[0];

    return storedParams;
  }

  return undefined;
};

export const useResetPasswordRouteParams = () => {
  if (storedNavigationParams.length > 0) {
    const params = storedNavigationParams.filter((p) => p.isLive);
    const storedParams = params[0];

    return storedParams;
  }

  return undefined;
};

export { ALL_MOBILE_ROUTES };
